import {
  useRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  MutableRefObject
} from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { getBoundingBoxStructured, getRotation } from './common'
import { Tooltip } from 'antd'
import { TranslationTextForTooltip } from 'admin/lib/Translations/TranslationTextForTooltip'

type OcrRegionsProps = {
  type: 'pdf' | 'image'
  ocrData: any
  width: number
  height: number
  rotation: number
  isRotated: boolean
  ocrEnabled: boolean
  ocrTooltipsEnabled: boolean
  handleExtractionValueClicked: (event: any) => void
  pageNumber: number
  attachmentIndex: number
  ocrSearchText: string
  fit?: string
  projectLocale: string | null
}

export const OcrRegions = ({
  type,
  ocrData,
  width: parentWidth,
  height: parentHeight,
  rotation,
  isRotated,
  ocrEnabled,
  ocrTooltipsEnabled,
  handleExtractionValueClicked,
  pageNumber,
  attachmentIndex,
  ocrSearchText = '',
  projectLocale
}: OcrRegionsProps) => {
  const containerRef = useRef<HTMLDivElement>()

  const pageOcrData = useMemo(() => {
    const NO_PAGE_NUMBER = 0
    return {
      height:
        Array.isArray(ocrData.height) && ocrData.height[pageNumber - 1]
          ? ocrData.height[pageNumber - 1]
          : ocrData.height,
      width:
        Array.isArray(ocrData.width) && ocrData.width[pageNumber - 1]
          ? ocrData.width[pageNumber - 1]
          : ocrData.width,
      subRegions: ocrData.subRegions.filter(
        ({ pageNumber: regionPageNumber }) =>
          // eslint-disable-next-line eqeqeq
          regionPageNumber == pageNumber || regionPageNumber == NO_PAGE_NUMBER
      )
    }
  }, [ocrData, pageNumber])

  return (
    <Container
      ref={containerRef as MutableRefObject<HTMLDivElement>}
      isPDF={type === 'pdf'}
      coordinates={{
        width: parentWidth,
        height: parentHeight
      }}
      rotation={rotation}
      isRotated={isRotated}
    >
      {ocrEnabled && (
        <OCRBoxes
          ocrData={pageOcrData}
          containerWidth={parentWidth}
          containerHeight={parentHeight}
          pageNumber={pageNumber}
          attachmentIndex={attachmentIndex}
          handleExtractionValueClicked={handleExtractionValueClicked}
          ocrSearchText={ocrSearchText}
          ocrTooltipsEnabled={ocrTooltipsEnabled}
          projectLocale={projectLocale}
        />
      )}
    </Container>
  )
}

interface RouteParams extends Record<string, string | undefined> {
  recordId: string | undefined
}

function OCRBoxes({
  ocrData,
  containerWidth,
  containerHeight,
  pageNumber,
  attachmentIndex,
  handleExtractionValueClicked,
  ocrSearchText,
  ocrTooltipsEnabled,
  projectLocale
}) {
  const [markedBoxes, setMarkedBoxes] = useState({})
  const lastFocus = useRef<HTMLInputElement>() as MutableRefObject<
    HTMLInputElement | undefined | null
  >
  const { recordId } = useParams<RouteParams>()
  const handleStart = useCallback(e => {
    lastFocus.current = document.activeElement as HTMLInputElement
  }, [])

  const handleEnd = useCallback(() => {
    if (lastFocus.current === document.activeElement) {
      lastFocus.current?.blur?.()
    }
    if (lastFocus.current) {
      lastFocus.current?.focus?.()
      lastFocus.current = undefined
    }
  }, [])

  const adjustXCoOrdinate = x => {
    return (x * containerWidth) / ocrData.width
  }

  const adjustYCoOrdinate = y => {
    return (y * containerHeight) / ocrData.height
  }

  const handleRightClick = (event, index: number): void => {
    setMarkedBoxes(prevState => {
      sessionStorage.setItem(
        `${recordId}-${attachmentIndex}-${pageNumber}-marked`,
        JSON.stringify({
          ...prevState,
          [index]: !prevState[index]
        })
      )
      return { ...prevState, [index]: !prevState[index] }
    })

    event.preventDefault()
    event.stopPropagation()
    handleEnd()
  }

  useEffect(() => {
    const storedMarked = sessionStorage.getItem(
      `${recordId}-${attachmentIndex}-${pageNumber}-marked`
    )
    if (storedMarked) {
      setMarkedBoxes(JSON.parse(storedMarked))
    }
  }, [attachmentIndex, pageNumber, recordId])

  const cleanText = (ocrText: string): string => {
    let cleanedText = ocrText
      .replace('<!-- FigureContent="', '')
      .replace('<!-- PageNumber="', '')
      .replace('<!-- PageHeader="', '')
      .replace('<!-- PageFooter="', '')
      .replace('" -->', '')
      .replace('===', '')
      .replace(/\\\*/g, '*')
      .replace(/\\_/g, '_')
      .replace('×', 'x')
    if (cleanedText.startsWith(': ')) {
      cleanedText = cleanedText.replace(': ', '')
    }
    return cleanedText
  }

  // Helper function to check if boxA is completely within boxB
  // Returns true only if boxA is well within boxB AND significantly smaller
  const isBoxWithinBox = (boxA, boxB) => {
    const getMinMax = box => {
      const minX = Math.min(
        box.topLeftX,
        box.topRightX,
        box.bottomRightX,
        box.bottomLeftX
      )
      const maxX = Math.max(
        box.topLeftX,
        box.topRightX,
        box.bottomRightX,
        box.bottomLeftX
      )
      const minY = Math.min(
        box.topLeftY,
        box.topRightY,
        box.bottomRightY,
        box.bottomLeftY
      )
      const maxY = Math.max(
        box.topLeftY,
        box.topRightY,
        box.bottomRightY,
        box.bottomLeftY
      )
      return { minX, maxX, minY, maxY }
    }

    const a = getMinMax(boxA)
    const b = getMinMax(boxB)

    // Add 1-pixel margin - boxA must be at least 1 pixel inside boxB on all sides
    const margin = 1
    const isWithinBounds =
      a.minX >= b.minX + margin &&
      a.maxX <= b.maxX - margin &&
      a.minY >= b.minY + margin &&
      a.maxY <= b.maxY - margin

    if (!isWithinBounds) {
      return false
    }

    // Only remove the larger box if the smaller box is significantly smaller
    // Calculate areas
    const areaA = (a.maxX - a.minX) * (a.maxY - a.minY)
    const areaB = (b.maxX - b.minX) * (b.maxY - b.minY)

    // Only return true if boxA is at least 30% smaller than boxB
    return areaA <= areaB * 0.7
  }

  // Filter out boxes that contain other boxes
  const filteredSubRegions = useMemo(() => {
    const regions = ocrData.subRegions.map((subRegion, index) => ({
      ...subRegion,
      originalIndex: index,
      boundingBox: getBoundingBoxStructured(subRegion.bounds.bounds)
    }))

    const indicesToRemove = new Set()

    // Normalize text for comparison (remove whitespace, newlines, punctuation, lowercase)
    const normalizeText = text => {
      return text.toLowerCase().replace(/[\s\n\r.,;:!?-]/g, '')
    }

    // Compare each box with every other box
    for (let i = 0; i < regions.length; i++) {
      for (let j = 0; j < regions.length; j++) {
        if (i !== j && !indicesToRemove.has(i)) {
          const boxI = regions[i].boundingBox
          const boxJ = regions[j].boundingBox

          // If box J is completely within box I spatially
          if (isBoxWithinBox(boxJ, boxI)) {
            // Only remove box I if the text content is similar/related
            // Check if the smaller box's text appears in the larger box's text
            const textI = normalizeText(regions[i].text)
            const textJ = normalizeText(regions[j].text)

            // Remove the larger box only if the smaller text is a substring
            // (indicating it's likely a duplicate OCR detection)
            if (textI.includes(textJ)) {
              indicesToRemove.add(i)
              break
            }
          }
        }
      }
    }

    return regions.filter((_, index) => !indicesToRemove.has(index))
  }, [ocrData.subRegions])

  return (
    <svg viewBox={`0 0 ${containerWidth} ${containerHeight}`}>
      {filteredSubRegions.map((region, index) => {
        const { boundingBox, originalIndex, text } = region
        const cleanedText = cleanText(text)
        const ocrBox = (
          <OCRBox
            data-testid="ocr-polygon"
            key={originalIndex}
            $searchMatch={
              ocrSearchText &&
              cleanedText.toLowerCase().includes(ocrSearchText.toLowerCase())
            }
            $marked={markedBoxes[originalIndex]}
            points={[
              adjustXCoOrdinate(boundingBox.topLeftX),
              adjustYCoOrdinate(boundingBox.topLeftY),
              adjustXCoOrdinate(boundingBox.topRightX),
              adjustYCoOrdinate(boundingBox.topRightY),
              adjustXCoOrdinate(boundingBox.bottomRightX),
              adjustYCoOrdinate(boundingBox.bottomRightY),
              adjustXCoOrdinate(boundingBox.bottomLeftX),
              adjustYCoOrdinate(boundingBox.bottomLeftY)
            ].join(',')}
            onMouseDown={handleStart}
            onClick={() => {
              // Store selected text in session storage so it can be read by line item field (if needed)

              sessionStorage.setItem('clipboard', cleanedText)

              handleEnd()
              handleExtractionValueClicked(cleanedText)

              // Clear session storage
              sessionStorage.removeItem('clipboard')
            }}
            onContextMenu={event => handleRightClick(event, originalIndex)}
            aria-label={cleanedText}
          />
        )

        if (ocrTooltipsEnabled) {
          return (
            <Tooltip
              title={
                <TranslationTextForTooltip
                  cleanedText={cleanedText}
                  projectLocale={projectLocale}
                />
              }
              key={originalIndex}
              placement={'right'}
            >
              {ocrBox}
            </Tooltip>
          )
        }

        return ocrBox
      })}
    </svg>
  )
}

const OCRBox = styled.polygon<{ $searchMatch?: boolean; $marked?: boolean }>`
  cursor: pointer;
  stroke: ${props =>
    props.$searchMatch
      ? 'rgb(121, 66, 230)'
      : props.$marked
        ? 'green'
        : 'grey'};
  stroke-width: ${props => (props.$searchMatch ? '2px' : '1px')};
  fill: ${props =>
    props.$marked
      ? 'rgba(0, 150, 0, 0.15)'
      : props.$searchMatch
        ? 'rgba(121, 66, 230, 0.1)'
        : 'rgba(150, 150, 150, 0.15)'};
`

const Container = styled.div<{
  isPDF: boolean
  coordinates: { width: number; height: number }
  rotation: number
  isRotated: boolean
}>`
  position: absolute;
  top: ${props => (props.isPDF || props.isRotated ? '50%' : '0')};
  left: ${props => (props.isPDF || props.isRotated ? '50%' : '0')};
  width: ${props => props.coordinates.width}px;
  height: ${props => props.coordinates.height}px;
  aspect-ratio: initial;
  max-height: none;
  transform: translate(
      ${props => (props.isPDF || props.isRotated ? '-50%, -50%' : '0,0')}
    )
    rotate(${props => getRotation(props.rotation)}deg);
`
